import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  TrendingUp, 
  X, 
  ChevronRight, 
  Zap,
  ArrowUp
} from "lucide-react";
import { ProgressionSuggestion } from "@/lib/auto-progression";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface ProgressionSuggestionsProps {
  suggestions: ProgressionSuggestion[];
  loading?: boolean;
  onDismiss?: (exerciseId: string) => void;
  onApply?: (suggestion: ProgressionSuggestion) => void;
  compact?: boolean;
  className?: string;
}

export function ProgressionSuggestions({
  suggestions,
  loading,
  onDismiss,
  onApply,
  compact = false,
  className
}: ProgressionSuggestionsProps) {
  if (loading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2].map(i => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  const confidenceStyles = {
    high: 'border-success/50 bg-success/5',
    medium: 'border-warning/50 bg-warning/5',
    low: 'border-muted',
  };

  const confidenceBadgeStyles = {
    high: 'bg-success/10 text-success border-success/20',
    medium: 'bg-warning/10 text-warning border-warning/20',
    low: 'bg-muted text-muted-foreground',
  };

  if (compact) {
    return (
      <div className={cn("space-y-2", className)}>
        {suggestions.slice(0, 3).map(suggestion => (
          <div
            key={suggestion.exerciseId}
            className={cn(
              "flex items-center justify-between p-3 rounded-lg border",
              confidenceStyles[suggestion.confidence]
            )}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-primary/10">
                <TrendingUp className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">{suggestion.exerciseName}</p>
                <p className="text-xs text-muted-foreground">
                  {suggestion.currentWeight}lbs → {suggestion.suggestedWeight}lbs
                </p>
              </div>
            </div>
            <Badge 
              variant="outline" 
              className={cn("text-xs", confidenceBadgeStyles[suggestion.confidence])}
            >
              {suggestion.confidence === 'high' ? 'Ready!' : 
               suggestion.confidence === 'medium' ? 'Consider' : 'Maybe'}
            </Badge>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Smart Progression
          </CardTitle>
          <Badge variant="secondary" className="text-xs">
            {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {suggestions.map(suggestion => (
          <div
            key={suggestion.exerciseId}
            className={cn(
              "p-4 rounded-lg border transition-all",
              confidenceStyles[suggestion.confidence]
            )}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold">{suggestion.exerciseName}</h4>
                  <Badge 
                    variant="outline" 
                    className={cn("mt-1 text-xs", confidenceBadgeStyles[suggestion.confidence])}
                  >
                    {suggestion.confidence} confidence
                  </Badge>
                </div>
              </div>
              {onDismiss && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onDismiss(suggestion.exerciseId)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="flex items-center gap-4 mb-3">
              <div className="flex items-center gap-2 text-lg">
                <span className="text-muted-foreground">{suggestion.currentWeight}lbs</span>
                <ArrowUp className="h-4 w-4 text-success" />
                <span className="font-bold text-success">{suggestion.suggestedWeight}lbs</span>
              </div>
              <span className="text-xs text-muted-foreground">
                +{suggestion.suggestedWeight - suggestion.currentWeight}lbs
              </span>
            </div>

            <p className="text-sm text-muted-foreground mb-3">
              {suggestion.reason}
            </p>

            {suggestion.recentSets.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-muted-foreground mb-2">Recent Performance</p>
                <div className="flex flex-wrap gap-1">
                  {suggestion.recentSets.slice(0, 6).map((set, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {set.weight}lbs × {set.reps}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {onApply && (
              <Button
                size="sm"
                className="w-full gap-2"
                onClick={() => onApply(suggestion)}
              >
                Apply Progression
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
